import { Chip, ChipProps, ThemeProvider, createTheme } from '@mui/material'
import { InterVariableFontFamilyTheme } from 'constants/styling/typographyTheme'
import React, { ReactElement } from 'react'

import { BKBNThemeWrapper } from '../BKBNThemeWrapper'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { badgeThemeOptions } from 'constants/styling/badgeTheme'

/**
 * @typedef BadgeSize Available size
 */
export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg'

/**
 * @typedef BadgeVariant Available variant
 */
export type BadgeVariant = 'filled' | 'outlined'

/**
 * @typedef BadgeColor Available color
 */
export type BadgeColor = 'gray' | 'darkGray' | 'beige' | 'blue' | 'coral' | 'green' | 'orange' | 'white'

/**
 * @interface Props Input properties
 */
interface Props extends ChipProps {
  /** The label of the badge. */
  label?: string
  /** The size of the badge. */
  size?: BadgeSize
  /** The variant style of the badge. */
  variant?: BadgeVariant,
  /** The color set of the badge. */
  color?: BadgeColor
  /** The icon on the left side of the badge label. */
  icon?: ReactElement
  /** Whether the badge should have fully rounded (pill) corners */
  rounded?: boolean
  /** Custom text color override for both default and selected states */
  textColor?: string
  /** Custom border color override (sets border to 1px solid with the given color) */
  borderColor?: string
  /** OnClick action on delete icon */
  onDelete?: () => void
}

/**
 * @component MUIBadge 
 * @example
 * <MUIBadge label="Chips" size="sm" color="blue" />
 */

export const MUIBadge: React.FC<Props> = ({
  label,
  size = 'md',
  variant = 'outlined',
  color = 'white',
  rounded = false,
  textColor,
  borderColor,
  icon,
  onDelete,
  sx,
  ...props
}) => {

  return (
    <BKBNThemeWrapper>
      <ThemeProvider theme={
        createTheme({
          ...InterVariableFontFamilyTheme,
          ...badgeThemeOptions
        })
      }>
        <Chip
          label={label}
          size={size}
          variant={variant}
          color={color}
          onDelete={onDelete}
          deleteIcon={<ClearRoundedIcon />}
          icon={icon}
          sx={{
            ...(rounded && { borderRadius: '2.4rem' }),
            ...(textColor && { color: textColor }),
            ...(borderColor && { border: `1px solid ${borderColor}` }),
            ...sx as object
          }}
          {...props}
        />
      </ThemeProvider>
    </BKBNThemeWrapper >
  )
}
